
import React from 'react';
import { CalendarClock } from 'lucide-react';
import { UserProfile } from '../types';
import { formatPhoneNumber } from '../utils/formatters';

interface OrderFormProps {
  totalCount: number;
  totalPrice: number;
  profile: UserProfile;
  onProfileChange: (profile: Partial<UserProfile>) => void;
  nameInputRef?: React.RefObject<HTMLInputElement | null>;
  phoneInputRef?: React.RefObject<HTMLInputElement | null>;
  onRequestChange?: (request: string) => void;
  requestValue?: string;
  showConsent?: boolean;
  isStoreOrder?: boolean;
  showPickupSection?: boolean;
  showRequestMessageSection?: boolean;
  pickupLabel?: string | null;
  pickupPolicyText?: string;
  pickupDateOptions?: Array<{ value: string; label: string }>;
  pickupHourOptions?: Array<{ value: number; label: string }>;
  selectedPickupDate?: string;
  selectedPickupHour?: number | '';
  isSavingPickup?: boolean;
  showPickupSaveButton?: boolean;
  onPickupDateChange?: (value: string) => void;
  onPickupHourChange?: (value: number) => void;
  onSavePickup?: () => void;
}

const OrderForm: React.FC<OrderFormProps> = ({
  totalCount,
  totalPrice,
  profile,
  onProfileChange,
  nameInputRef,
  phoneInputRef,
  onRequestChange,
  requestValue = '',
  showConsent = true,
  isStoreOrder = false,
  showPickupSection = false,
  showRequestMessageSection = false,
  pickupLabel,
  pickupPolicyText,
  pickupDateOptions = [],
  pickupHourOptions = [],
  selectedPickupDate = '',
  selectedPickupHour = '',
  isSavingPickup = false,
  showPickupSaveButton = true,
  onPickupDateChange,
  onPickupHourChange,
  onSavePickup,
}) => {
  const hasPickupPolicy =
    isStoreOrder &&
    showPickupSection &&
    pickupDateOptions.length > 0 &&
    pickupHourOptions.length > 0;
  const showStoreSection =
    isStoreOrder && (showPickupSection || showRequestMessageSection);
  const emptyPickupLabelText = showPickupSaveButton
    ? '저장된 픽업 일정이 없습니다. 방문 시간을 먼저 저장해주세요.'
    : '방문 시간을 선택해주세요.';
  const storeSectionTitle = showPickupSection && showRequestMessageSection
    ? '픽업 일정 및 요청사항'
    : showPickupSection
      ? '픽업 일정'
      : '요청사항';

  return (
    <div className="flex flex-col">
      <div className="mx-6 gap-y-1 py-5 border-b flex flex-col text-sm">
        <div className="flex justify-between">
          <span className="text-gray-500 font-medium">주문수량</span>
          <span className="font-bold">{totalCount}개</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500 font-medium">주문금액</span>
          <span className="font-bold">{totalPrice.toLocaleString()}원</span>
        </div>
        <div className="flex justify-between items-baseline mt-1">
          <span className="font-black text-gray-800">합계</span>
          <span className="font-black text-xl text-emerald-600">{totalPrice.toLocaleString()}원</span>
        </div>
      </div>

      {/* Inputs */}
      <div className="mx-6 py-5 flex flex-col space-y-4">
        <div className="relative group">
          <label className="text-[11px] font-black text-gray-400 uppercase tracking-wider mb-1 block">고객명 <span className="text-red-500">*</span></label>
          <input
            ref={nameInputRef}
            id="buyer-name-input"
            type="text"
            value={profile.name}
            onChange={(e) => onProfileChange({ name: e.target.value })}
            placeholder="최초 1회만 입력해주세요."
            className="w-full h-12 bg-gray-50 border border-gray-200 rounded-xl px-4 text-sm font-bold focus:outline-none focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-500 transition-all"
          />
        </div>
        <div className="relative">
          <label className="text-[11px] font-black text-gray-400 uppercase tracking-wider mb-1 block">전화번호 <span className="text-red-500">*</span></label>
          <input
            ref={phoneInputRef}
            id="buyer-phone-input"
            type="tel"
            value={formatPhoneNumber(profile.phone)}
            onChange={(e) => onProfileChange({ phone: e.target.value })}
            placeholder="010-0000-0000"
            className="w-full h-12 bg-gray-50 border border-gray-200 rounded-xl px-4 text-sm font-bold focus:outline-none focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-500 transition-all"
          />
        </div>

        {showStoreSection && (
          <div className="rounded-[24px] border border-gray-200 p-4 space-y-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <CalendarClock size={16} className="text-emerald-600" />
                <p className="text-[11px] font-black text-gray-700 uppercase tracking-wider">
                  {storeSectionTitle}
                </p>
              </div>
              {pickupPolicyText && (
                <p className="text-[11px] font-bold text-gray-700 mb-3">
                  운영 기준: {pickupPolicyText}
                </p>
              )}
              {showPickupSection && (
                <>
                  <p className="text-[13px] font-black text-gray-900 mb-2">
                    {pickupLabel?.trim() || emptyPickupLabelText}
                  </p>
                  {hasPickupPolicy ? (
                    <div className="space-y-2">
                      <select
                        value={selectedPickupHour === '' ? '' : selectedPickupHour}
                        onChange={(e) => onPickupHourChange?.(Number(e.target.value))}
                        className="h-11 w-full px-4 rounded-2xl border border-gray-200 bg-white text-sm font-bold outline-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-400"
                      >
                        {pickupHourOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <p className="text-[11px] font-bold text-gray-700">
                        날짜는 가장 빠른 가능 일정으로 자동 적용됩니다.
                      </p>
                      {showPickupSaveButton && (
                        <button
                          onClick={() => onSavePickup?.()}
                          disabled={
                            !onSavePickup ||
                            isSavingPickup ||
                            selectedPickupHour === ''
                          }
                          className="w-full h-11 rounded-2xl bg-gray-500 text-white text-sm font-black disabled:bg-emerald-300"
                        >
                          {isSavingPickup
                            ? '저장 중...'
                            : pickupLabel?.trim()
                              ? '픽업 일정 업데이트'
                              : '픽업 일정 저장'}
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="rounded-2xl border border-dashed border-gray-200 bg-white px-4 py-3 text-[12px] font-bold text-emerald-700">
                      판매자가 아직 운영 요일/시간을 설정하지 않았습니다.
                    </div>
                  )}
                </>
              )}
            </div>

            {showRequestMessageSection && (
              <div className={showPickupSection ? 'border-t border-gray-200 pt-4' : ''}>
                <label className="text-[11px] font-black text-gray-700 uppercase tracking-wider block mb-2">
                  사장님께 전달할 메시지
                </label>
                <textarea
                  value={requestValue}
                  onChange={(e) => onRequestChange?.(e.target.value)}
                  placeholder="문 앞 전달, 도착 전 연락 등 필요한 내용을 남겨주세요."
                  rows={4}
                  className="w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm font-bold text-gray-700 outline-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-400 resize-none"
                />
                <p className="mt-2 text-[11px] font-bold text-gray-700">
                  입력한 메시지는 주문서 요청사항에 함께 전달됩니다.
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {showConsent && (
        <div className="mx-6 py-5 mb-24">
          <div className="text-sm font-black text-gray-800 mb-2">개인정보 수집 및 이용 동의</div>
          <div className="p-4 bg-gray-50 rounded-2xl text-[11px] text-gray-400 font-bold leading-relaxed border border-gray-100">
            입력해주신 개인정보를 활용합니다.<br />
            1. 수집 목적: 공동구매 서비스 제공 및 주문 접수<br />
            2. 수집 항목: 이름, 전화번호, 주문 내역<br />
            3. 보유 기간 : 서비스 이용 종료 시까지 (최대 2년)
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderForm;
